<template>
  <section class="facility-page" :dir="isArabic ? 'rtl' : 'ltr'">
    <router-link to="/facilities" class="back-link">
      {{ isArabic ? "→" : "←" }}

      {{ t("facilityDetails.backToFacilities") }}
    </router-link>

    <!-- Loading -->

    <div v-if="loading" class="loading-wrapper">
      <div class="loading-card">
        <v-progress-circular
          indeterminate
          size="70"
          width="6"
          color="primary"
        />

        <h3>{{ t("facilityDetails.loadingTitle") }}</h3>

        <p>{{ t("facilityDetails.loadingDescription") }}</p>
      </div>
    </div>

    <!-- Error -->

    <div v-else-if="errorMessage" class="error-box">
      {{ errorMessage }}
    </div>

    <!-- Facility details -->

    <template v-else-if="facility">
      <!-- Hero -->

      <div class="hero-card">
        <div class="hero-content">
          <h1>{{ facilityName }}</h1>

          <div class="meta-grid">
            <div>
              {{ t("facilityDetails.fields.type") }}:

              <strong>{{ facilityType }}</strong>
            </div>

            <div>
              {{ t("facilityDetails.fields.governorate") }}:

              <strong>{{ governorateName }}</strong>
            </div>

            <div>
              {{ t("facilityDetails.fields.district") }}:

              <strong>{{ districtName }}</strong>
            </div>

            <div>
              {{ t("facilityDetails.fields.operationalStatus") }}:

              <strong>{{ operationalStatus }}</strong>
            </div>

            <div>
              {{ t("facilityDetails.fields.staff") }}:

              <strong>{{ totalStaff }}</strong>
            </div>

            <div>
              {{ t("facilityDetails.fields.beneficiaries") }}:

              <strong>{{ beneficiaries }}</strong>
            </div>

            <div>
              {{ t("facilityDetails.fields.population") }}:

              <strong>{{ targetPopulation }}</strong>
            </div>
          </div>
        </div>

        <div class="score-box" :class="priorityClass">
          <div class="score">{{ score }}</div>

          <div class="priority">{{ priorityText }}</div>

          <span>{{ interventionTime }}</span>
        </div>
      </div>

      <!-- Decision scenarios -->

      <div class="alert-box">
        <strong>{{ t("facilityDetails.decisionScenariosTitle") }}</strong>

        {{ decisionScenarios }}
      </div>

      <!-- Findings and interventions -->

      <div class="info-grid">
        <div class="info-card">
          <h3>{{ t("facilityDetails.fieldFindingsTitle") }}</h3>

          <p>{{ fieldFindings }}</p>
        </div>

        <div class="info-card">
          <h3>{{ t("facilityDetails.proposedInterventionTitle") }}</h3>

          <p>{{ proposedIntervention }}</p>
        </div>
      </div>

      <!-- Priority summary -->

      <div class="cost-total">
        {{ t("facilityDetails.priorityScore") }}:

        <strong>{{ priorityCode }}</strong>

        <span>{{ t("facilityDetails.basedOnFis") }}</span>
      </div>

      <!-- Facility summary -->

      <div class="table-card">
        <h3>{{ t("facilityDetails.facilitySummaryTitle") }}</h3>

        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{{ t("facilities.table.facility") }}</th>

                <th>{{ t("facilities.table.type") }}</th>

                <th>{{ t("facilities.table.governorate") }}</th>

                <th>{{ t("facilities.table.district") }}</th>

                <th>{{ t("facilities.table.operationalStatus") }}</th>

                <th>{{ t("facilities.table.staff") }}</th>

                <th>{{ t("facilities.table.beneficiaries") }}</th>

                <th>{{ t("facilities.table.population") }}</th>

                <th>FIS</th>

                <th>{{ t("facilities.table.priority") }}</th>
              </tr>
            </thead>

            <tbody>
              <tr>
                <td>{{ facilityName }}</td>

                <td>{{ facilityType }}</td>

                <td>{{ governorateName }}</td>

                <td>{{ districtName }}</td>

                <td>{{ operationalStatus }}</td>

                <td>{{ totalStaff }}</td>

                <td>{{ beneficiaries }}</td>

                <td>{{ targetPopulation }}</td>

                <td>
                  <strong>{{ score }}</strong>
                </td>

                <td>
                  <span class="priority-pill" :class="priorityCode">
                    {{ priorityCode }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- FIS components -->

      <div class="table-card">
        <h3>{{ t("facilityDetails.fisComponentsTitle") }}</h3>

        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th>
                  {{ t("facilityDetails.components.structuralDamage") }}
                </th>

                <th>
                  {{ t("facilityDetails.components.serviceImpact") }}
                </th>

                <th>
                  {{ t("facilityDetails.components.populationAccess") }}
                </th>

                <th>
                  {{ t("facilityDetails.components.feasibility") }}
                </th>

                <th>
                  {{ t("facilityDetails.components.equity") }}
                </th>

                <th>
                  {{ t("facilityDetails.components.result") }}
                </th>
              </tr>
            </thead>

            <tbody>
              <tr>
                <td>{{ fisComponents.structural }}</td>

                <td>{{ fisComponents.services }}</td>

                <td>{{ fisComponents.population }}</td>

                <td>{{ fisComponents.feasibility }}</td>

                <td>{{ fisComponents.equity }}</td>

                <td>
                  <strong>{{ score }}</strong>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Indicators -->

      <div class="table-card">
        <h3>{{ t("facilityDetails.indicatorsTitle") }}</h3>

        <div
          v-for="group in indicators"
          :key="group.title"
          class="indicator-group"
        >
          <h4>{{ group.title }}</h4>

          <div
            v-for="item in group.items"
            :key="item.code"
            class="indicator-row"
          >
            <div class="indicator-info">
              <span class="code">{{ item.code }}</span>

              <strong>{{ item.name }}</strong>

              <small>{{ item.standard }}</small>
            </div>

            <div class="indicator-value">
              {{ item.value }}
            </div>

            <div class="bar">
              <div
                class="bar-fill"
                :style="{
                  width: getBarWidth(item.value) + '%',
                }"
              ></div>
            </div>

            <div class="max">100</div>
          </div>
        </div>
      </div>
    </template>

    <div v-else class="error-box">
      {{ t("facilityDetails.noData") }}
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";

import { useRoute } from "vue-router";

import { useI18n } from "vue-i18n";

import api from "@/plugins/api";

const route = useRoute();

const { t, locale } = useI18n();

const loading = ref(true);

const errorMessage = ref("");

const facility = ref(null);

const isArabic = computed(() => locale.value === "ar");

/*

|--------------------------------------------------------------------------

| Helpers

|--------------------------------------------------------------------------

*/

const safe = (value, fallback = "-") => {
  return value !== null && value !== undefined && value !== ""
    ? value
    : fallback;
};

const formatNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  const number = Number(value);

  if (!Number.isFinite(number)) {
    return "-";
  }

  return number.toLocaleString(isArabic.value ? "en-US" : "en-US");
};

const getBarWidth = (value) => {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return 0;
  }

  return Math.min(Math.max(number, 0), 100);
};

const parseJson = (value, fallback = null) => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  if (typeof value === "object") {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    console.warn("Failed to parse facility report JSON:", error);

    return fallback;
  }
};

const getApiIndicators = () => {
  if (Array.isArray(facility.value?.indicators)) {
    return facility.value.indicators;
  }

  const parsed = parseJson(facility.value?.indicators_json, []);

  return Array.isArray(parsed) ? parsed : [];
};

const getAxisScore = (axisCode) => {
  const axes = parseJson(facility.value?.axes_json, {});

  const directScore = Number(axes?.[axisCode]?.score);

  if (Number.isFinite(directScore)) {
    return directScore.toFixed(1);
  }

  const values = getApiIndicators()
    .filter((item) => (item.axis_code || item.axis) === axisCode)

    .map((item) =>
      Number(item.normalized_score ?? item.indicator_value ?? item.value)
    )

    .filter((value) => Number.isFinite(value));

  if (!values.length) {
    return 0;
  }

  const average = values.reduce((sum, value) => sum + value, 0) / values.length;

  return average.toFixed(1);
};

/*

|--------------------------------------------------------------------------

| Localized facility fields

|--------------------------------------------------------------------------

*/

const facilityName = computed(() => {
  if (!facility.value) {
    return "-";
  }

  return isArabic.value
    ? safe(
        facility.value.facility_name_ar ||
          facility.value.facility_name_en ||
          facility.value.facility_name
      )
    : safe(
        facility.value.facility_name_en ||
          facility.value.facility_name_ar ||
          facility.value.facility_name
      );
});

const facilityType = computed(() => {
  if (!facility.value) {
    return "-";
  }

  return isArabic.value
    ? safe(
        facility.value.facility_type_ar ||
          facility.value.facility_type_en ||
          facility.value.facility_type
      )
    : safe(
        facility.value.facility_type_en ||
          facility.value.facility_type_ar ||
          facility.value.facility_type
      );
});

const governorateName = computed(() => {
  if (!facility.value) {
    return "-";
  }

  return isArabic.value
    ? safe(
        facility.value.governorate_name_ar ||
          facility.value.governorate_name_en ||
          facility.value.governorate
      )
    : safe(
        facility.value.governorate_name_en ||
          facility.value.governorate_name_ar ||
          facility.value.governorate
      );
});

const districtName = computed(() => {
  if (!facility.value) {
    return "-";
  }

  return isArabic.value
    ? safe(
        facility.value.district_name_ar ||
          facility.value.district_name_en ||
          facility.value.district
      )
    : safe(
        facility.value.district_name_en ||
          facility.value.district_name_ar ||
          facility.value.district
      );
});

const operationalStatus = computed(() => {
  if (!facility.value) {
    return "-";
  }

  return isArabic.value
    ? safe(
        facility.value.operational_status_ar ||
          facility.value.operational_status_en ||
          facility.value.operational_status
      )
    : safe(
        facility.value.operational_status_en ||
          facility.value.operational_status_ar ||
          facility.value.operational_status
      );
});

/*

|--------------------------------------------------------------------------

| Numeric fields

|--------------------------------------------------------------------------

*/

const totalStaff = computed(() => {
  return formatNumber(
    facility.value?.cadre_total_count ?? facility.value?.total_staff
  );
});

const beneficiaries = computed(() => {
  return formatNumber(
    facility.value?.beneficiaries_current ?? facility.value?.beneficiaries_month
  );
});

const targetPopulation = computed(() => {
  return formatNumber(facility.value?.target_population);
});

const score = computed(() => {
  const value =
    facility.value?.FIS_result ??
    facility.value?.FIS ??
    facility.value?.fis ??
    0;

  const number = Number(value);

  return Number.isFinite(number) ? number.toFixed(1) : "0.0";
});

const priorityCode = computed(() => {
  return (
    facility.value?.priority_code ||
    facility.value?.priority ||
    facility.value?.periority ||
    "P4"
  );
});

const priorityClass = computed(() => {
  return `score-${priorityCode.value}`;
});

/*

|--------------------------------------------------------------------------

| Priority text

|--------------------------------------------------------------------------

*/

const priorityText = computed(() => {
  if (isArabic.value && facility.value?.risk_level) {
    return facility.value.risk_level;
  }

  return t(`facilityDetails.priorities.${priorityCode.value}`);
});

const interventionTime = computed(() => {
  if (isArabic.value && facility.value?.timeframe) {
    return facility.value.timeframe;
  }

  return t(`facilityDetails.interventionTimes.${priorityCode.value}`);
});

/*

|--------------------------------------------------------------------------

| Decision scenarios

|--------------------------------------------------------------------------

*/

const decisionScenarios = computed(() => {
  const scenarios = parseJson(facility.value?.scenarios_json, []);

  if (Array.isArray(scenarios)) {
    const activeScenarios = scenarios.filter(
      (scenario) => Number(scenario.active) === 1 || scenario.active === true
    );

    if (activeScenarios.length) {
      return activeScenarios

        .map(
          (scenario) =>
            scenario.action || scenario.description || scenario.title
        )

        .filter(Boolean)

        .join(" — ");
    }
  }

  if (priorityCode.value === "P1") {
    return t("facilityDetails.scenarios.P1");
  }

  if (priorityCode.value === "P2") {
    return t("facilityDetails.scenarios.P2");
  }

  if (priorityCode.value === "P3") {
    return t("facilityDetails.scenarios.P3");
  }

  return t("facilityDetails.scenarios.P4");
});

/*

|--------------------------------------------------------------------------

| Findings

|--------------------------------------------------------------------------

*/

const fieldFindings = computed(() => {
  if (facility.value?.executive_summary) {
    return facility.value.executive_summary;
  }

  return t("facilityDetails.findingsText", {
    status: operationalStatus.value,

    staff: totalStaff.value,

    beneficiaries: beneficiaries.value,

    population: targetPopulation.value,
  });
});

/*

|--------------------------------------------------------------------------

| Proposed intervention

|--------------------------------------------------------------------------

*/

const proposedIntervention = computed(() => {
  const interventions = parseJson(facility.value?.interventions_json, []);

  if (Array.isArray(interventions) && interventions.length) {
    return interventions

      .slice(0, 3)

      .map((intervention) => intervention.description || intervention.title)

      .filter(Boolean)

      .join(" — ");
  }

  return t(`facilityDetails.interventions.${priorityCode.value}`);
});

/*

|--------------------------------------------------------------------------

| FIS components

|--------------------------------------------------------------------------

*/

const fisComponents = computed(() => {
  return {
    structural: safe(
      facility.value?.structural_risk_score ??
        facility.value?.structural_score ??
        facility.value?.structural_damage ??
        getAxisScore("INF")
    ),

    services: safe(
      facility.value?.service_score ??
        facility.value?.services_score ??
        getAxisScore("SRV")
    ),

    population: safe(
      facility.value?.population_score ?? facility.value?.population_access ?? 0
    ),

    feasibility: safe(
      facility.value?.financial_axis_score ??
        facility.value?.feasibility_score ??
        facility.value?.feas_score ??
        getAxisScore("FIN")
    ),

    equity: safe(
      facility.value?.equity_score ??
        facility.value?.equl_score ??
        getAxisScore("EQ")
    ),
  };
});

/*

|--------------------------------------------------------------------------

| Indicators

|--------------------------------------------------------------------------

*/

const indicators = computed(() => {
  const apiIndicators = getApiIndicators();

  const axisOrder = ["INF", "SRV", "HRH", "FIN", "GOV", "EQ"];

  const axisTitles = {
    INF: isArabic.value ? "البنية التحتية" : "Infrastructure",

    SRV: isArabic.value ? "الخدمات الصحية" : "Health services",

    HRH: isArabic.value ? "القوى العاملة الصحية" : "Health workforce",

    FIN: isArabic.value ? "التمويل" : "Finance",

    GOV: isArabic.value ? "الحوكمة" : "Governance",

    EQ: isArabic.value ? "العدالة" : "Equity",
  };

  return axisOrder

    .map((axisCode) => {
      const items = apiIndicators

        .filter((item) => (item.axis_code || item.axis) === axisCode)

        .map((item, index) => {
          const value = Number(
            item.normalized_score ?? item.indicator_value ?? item.value ?? 0
          );

          const target =
            item.target_value !== null && item.target_value !== undefined
              ? `${isArabic.value ? "الهدف" : "Target"}: ${item.target_value}${
                  item.unit || ""
                }`
              : "";

          const classification = item.classification || item.status || "";

          return {
            code: item.code || `${axisCode}-${index + 1}`,

            name: item.indicator_name || item.name || "-",

            value: Number.isFinite(value) ? Number(value.toFixed(1)) : 0,

            standard:
              [classification, target].filter(Boolean).join(" — ") || "-",
          };
        });

      return {
        title: axisTitles[axisCode],

        items,
      };
    })

    .filter((group) => group.items.length > 0);
});

/*

|--------------------------------------------------------------------------

| Load facility

|--------------------------------------------------------------------------

*/

const loadFacility = async () => {
  loading.value = true;

  errorMessage.value = "";

  facility.value = null;

  try {
    const routeIdentifier = Array.isArray(route.params.id)
      ? route.params.id[0]
      : route.params.id;

    const identifier = String(routeIdentifier ?? "").trim();

    if (!identifier) {
      throw new Error("Facility identifier is missing");
    }

    const response = await api.get(
      `/health-facilities/${encodeURIComponent(identifier)}/details`
    );

    const data = response.data?.data ?? response.data;

    if (!data || typeof data !== "object" || Array.isArray(data)) {
      throw new Error("Invalid API response");
    }

    facility.value = data;
  } catch (error) {
    console.error("Failed to load facility:", error);

    if (error.response?.status === 404) {
      errorMessage.value = t("facilityDetails.facilityNotFound");
    } else {
      errorMessage.value = t("facilityDetails.loadError");
    }
  } finally {
    loading.value = false;
  }
};

/*

|--------------------------------------------------------------------------

| Print

|--------------------------------------------------------------------------

*/

onMounted(loadFacility);
</script>

<style scoped>
.facility-page {
  min-height: 100vh;

  padding: 28px 30px 70px;

  color: #172033;

  background: #f4f6f9;

  font-family: "Cairo";
}

.back-link {
  display: inline-block;

  margin-bottom: 18px;

  color: #173f73;

  font-weight: 800;

  text-decoration: none;
}

.back-link:hover {
  text-decoration: underline;
}

/*

|--------------------------------------------------------------------------

| Loading

|--------------------------------------------------------------------------

*/

.loading-wrapper {
  position: fixed;

  inset: 0;

  z-index: 99999;

  display: flex;

  align-items: center;

  justify-content: center;

  background: rgba(255, 255, 255, 0.93);

  backdrop-filter: blur(5px);
}

.loading-card {
  width: 320px;

  max-width: 90%;

  padding: 34px 24px;

  text-align: center;

  background: #fff;

  border: 1px solid #dde4ec;

  border-radius: 20px;

  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.12);
}

.loading-card h3 {
  margin: 18px 0 0;

  font-size: 18px;

  font-weight: 800;

  color: #0f172a;
}

.loading-card p {
  margin: 8px 0 0;

  font-size: 13px;

  color: #64748b;
}

/*

|--------------------------------------------------------------------------

| Error

|--------------------------------------------------------------------------

*/

.error-box {
  padding: 25px;

  color: #b91c1c;

  text-align: center;

  font-size: 14px;

  font-weight: 800;

  background: #fff1f1;

  border: 1px solid #fecaca;

  border-radius: 10px;
}

/*

|--------------------------------------------------------------------------

| Cards

|--------------------------------------------------------------------------

*/

.hero-card,
.info-card,
.table-card,
.action-card {
  background: #fff;

  border: 1px solid #dde4ec;

  border-radius: 14px;

  box-shadow: 0 1px 3px rgba(13, 34, 64, 0.08);
}

.hero-card {
  display: flex;

  align-items: stretch;

  justify-content: space-between;

  gap: 20px;

  padding: 24px;

  margin-bottom: 16px;
}

.hero-content {
  flex: 1;
}

.hero-card h1 {
  margin: 0 0 18px;

  color: #0f172a;

  font-size: 28px;

  font-weight: 900;
}

.meta-grid {
  display: grid;

  grid-template-columns: repeat(2, minmax(0, 1fr));

  gap: 12px 28px;

  color: #475569;

  font-size: 14px;
}

.meta-grid strong {
  color: #111827;
}

/*

|--------------------------------------------------------------------------

| Score

|--------------------------------------------------------------------------

*/

.score-box {
  min-width: 210px;

  padding: 20px;

  text-align: center;

  border-radius: 16px;
}

.score-P1 {
  background: #fee2e2;

  border: 1px solid #fecaca;
}

.score-P2 {
  background: #fff7ed;

  border: 1px solid #fed7aa;
}

.score-P3 {
  background: #eff6ff;

  border: 1px solid #bfdbfe;
}

.score-P4 {
  background: #ecfdf5;

  border: 1px solid #bbf7d0;
}

.score {
  direction: ltr;

  color: #b45309;

  font-family: "Courier New", monospace;

  font-size: 48px;

  font-weight: 900;

  line-height: 1;
}

.priority {
  margin-top: 12px;

  color: #9a3412;

  font-weight: 900;
}

.score-box span {
  display: block;

  margin-top: 8px;

  color: #64748b;

  font-size: 13px;
}

/*

|--------------------------------------------------------------------------

| Alert

|--------------------------------------------------------------------------

*/

.alert-box {
  padding: 16px 18px;

  margin-bottom: 18px;

  color: #b91c1c;

  font-size: 14px;

  font-weight: 700;

  background: #fff1f1;

  border: 1px solid #fecaca;

  border-radius: 12px;
}

/*

|--------------------------------------------------------------------------

| Information

|--------------------------------------------------------------------------

*/

.info-grid {
  display: grid;

  grid-template-columns: repeat(2, minmax(0, 1fr));

  gap: 18px;

  margin-bottom: 18px;
}

.info-card {
  padding: 20px;
}

.info-card h3,
.table-card h3,
.action-card h3 {
  margin: 0 0 12px;

  color: #0f172a;

  font-size: 18px;

  font-weight: 900;
}

.info-card p {
  margin: 0;

  color: #475569;

  font-size: 14px;

  line-height: 2;
}

/*

|--------------------------------------------------------------------------

| Priority summary

|--------------------------------------------------------------------------

*/

.cost-total {
  padding: 18px 22px;

  margin-bottom: 18px;

  color: #fff;

  font-size: 17px;

  font-weight: 800;

  background: #173f73;

  border-radius: 14px;
}

.cost-total strong {
  direction: ltr;

  display: inline-block;

  margin: 0 8px;

  font-size: 24px;
}

.cost-total span {
  color: #dbeafe;

  font-size: 13px;
}

/*

|--------------------------------------------------------------------------

| Tables

|--------------------------------------------------------------------------

*/

.table-card {
  padding: 20px;

  margin-bottom: 18px;
}

.table-wrap {
  overflow-x: auto;
}

table {
  width: 100%;

  min-width: 900px;

  border-collapse: collapse;
}

thead {
  background: #f1f5f9;
}

th,
td {
  padding: 14px;

  font-size: 13px;

  border-bottom: 1px solid #e5e7eb;
}

.facility-page[dir="rtl"] th,
.facility-page[dir="rtl"] td {
  text-align: right;
}

.facility-page[dir="ltr"] th,
.facility-page[dir="ltr"] td {
  text-align: left;
}

th {
  color: #1e293b;

  font-weight: 900;
}

td {
  color: #334155;
}

/*

|--------------------------------------------------------------------------

| Priority badges

|--------------------------------------------------------------------------

*/

.priority-pill {
  display: inline-block;

  min-width: 60px;

  padding: 7px 15px;

  color: #fff;

  text-align: center;

  font-size: 13px;

  font-weight: 900;

  border-radius: 999px;
}

.priority-pill.P1 {
  background: #dc2626;
}

.priority-pill.P2 {
  background: #ea580c;
}

.priority-pill.P3 {
  background: #2563eb;
}

.priority-pill.P4 {
  background: #16a34a;
}

/*

|--------------------------------------------------------------------------

| Indicators

|--------------------------------------------------------------------------

*/

.indicator-group {
  margin-top: 22px;
}

.indicator-group h4 {
  padding: 12px 14px;

  margin: 0 0 12px;

  font-size: 15px;

  font-weight: 900;

  background: #f8fafc;

  border-radius: 10px;
}

.facility-page[dir="rtl"] .indicator-group h4 {
  border-right: 5px solid #173f73;
}

.facility-page[dir="ltr"] .indicator-group h4 {
  border-left: 5px solid #173f73;
}

.indicator-row {
  display: grid;

  grid-template-columns:
    minmax(240px, 1.8fr)
    70px
    minmax(180px, 1fr)
    45px;

  gap: 14px;

  align-items: center;

  padding: 14px 0;

  border-bottom: 1px solid #edf2f7;
}

.indicator-info {
  display: flex;

  flex-direction: column;

  gap: 5px;
}

.code {
  color: #173f73;

  font-size: 13px;

  font-weight: 900;
}

.indicator-info small {
  color: #64748b;

  line-height: 1.7;
}

.indicator-value {
  direction: ltr;

  color: #0f172a;

  text-align: center;

  font-family: "Courier New", monospace;

  font-size: 22px;

  font-weight: 900;
}

.bar {
  height: 12px;

  overflow: hidden;

  background: #e2e8f0;

  border-radius: 999px;
}

.bar-fill {
  height: 100%;

  background: #173f73;

  border-radius: 999px;
}

.max {
  direction: ltr;

  color: #64748b;

  font-weight: 800;
}

/*

|--------------------------------------------------------------------------

| PDF action

|--------------------------------------------------------------------------

*/

.action-card {
  display: flex;

  align-items: center;

  justify-content: space-between;

  gap: 18px;

  padding: 22px;
}

.action-card p {
  margin: 0;

  color: #64748b;

  font-size: 14px;
}

.pdf-btn {
  padding: 13px 22px;

  color: #111827;

  font-weight: 900;

  cursor: pointer;

  background: #b8923e;

  border: none;

  border-radius: 10px;
}

.pdf-btn:hover {
  background: #a97f2f;
}

/*

|--------------------------------------------------------------------------

| Responsive

|--------------------------------------------------------------------------

*/

@media (max-width: 900px) {
  .facility-page {
    padding: 18px 12px 45px;
  }

  .hero-card,
  .action-card {
    display: block;
  }

  .info-grid {
    grid-template-columns: 1fr;
  }

  .score-box {
    margin-top: 18px;
  }

  .meta-grid {
    grid-template-columns: 1fr;
  }

  .indicator-row {
    grid-template-columns: 1fr;
  }

  .action-card .pdf-btn {
    width: 100%;

    margin-top: 16px;
  }
}

/*

|--------------------------------------------------------------------------

| Print

|--------------------------------------------------------------------------

*/

@media print {
  .back-link,
  .pdf-btn {
    display: none;
  }

  .facility-page {
    padding: 0;

    background: #fff;
  }

  .hero-card,
  .info-card,
  .table-card,
  .action-card {
    box-shadow: none;

    break-inside: avoid;
  }
}
</style>
